import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { Campo } from '@/components/Campo';
import { Encabezado } from '@/components/Estados';

import { FormLicencia, licenciaVacia } from './FormLicencia';
import type { AlumnoDetalle } from './queries';

const schema = z.object({
  name: z.string().trim().min(2, 'Escribe el nombre').max(100),
  email: z.string().trim().email('Escribe un correo válido').max(120),
  phone: z.string().trim().max(20),
  gender: z.enum(['', 'M', 'H', 'N', 'S']),
  targetSpecialty: z.string().trim().max(80),
  schoolName: z.string().trim().max(150),
  sendInvite: z.boolean(),
});
type Form = z.infer<typeof schema>;

function Nuevo() {
  const navigate = useNavigate();
  const [conLicencia, setConLicencia] = useState(false);
  const [licencia, setLicencia] = useState(licenciaVacia);
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      gender: '',
      targetSpecialty: '',
      schoolName: '',
      sendInvite: true,
    },
  });
  const { errors } = form.formState;

  const crear = useMutation({
    mutationFn: (v: Form) => {
      if (conLicencia && (!licencia.membershipId || !licencia.endsOn)) {
        throw new Error('Elige la licencia y su fecha final, o quita la licencia.');
      }
      const membership = { ...licencia };
      delete membership.notify;
      return unwrap<AlumnoDetalle>(
        api.POST('/api/v1/admin/users', {
          body: {
            name: v.name,
            email: v.email,
            sendInvite: v.sendInvite,
            phone: v.phone || null,
            gender: v.gender || null,
            targetSpecialty: v.targetSpecialty || null,
            schoolName: v.schoolName || null,
            ...(conLicencia && { membership }),
          },
        }),
      );
    },
    onSuccess: (a) => {
      toast.success(
        `Alumno creado${form.getValues('sendInvite') ? '; le enviamos el enlace para definir su contraseña' : ''}.`,
      );
      void navigate(`/alumnos/${a.id}`, { replace: true });
    },
    onError: (e) =>
      toast.error(e instanceof Error && e.name !== 'ApiError' ? e.message : mensajeDe(e)),
  });

  return (
    <>
      <Link to="/alumnos" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold">
        <ArrowLeft size={16} aria-hidden /> Alumnos
      </Link>
      <Encabezado
        titulo="Nuevo alumno"
        descripcion="La cuenta se crea sin contraseña: el alumno recibe un enlace para definirla."
      />
      <form
        className="grid gap-6 xl:grid-cols-2"
        onSubmit={(e) => void form.handleSubmit((v) => crear.mutate(v))(e)}
        noValidate
      >
        <section className="panel-vidrio space-y-4">
          <h2 className="text-lg font-extrabold">Datos</h2>
          <Campo etiqueta="Nombre completo" error={errors.name?.message}>
            <input className="input-vidrio" {...form.register('name')} />
          </Campo>
          <Campo etiqueta="Correo" error={errors.email?.message}>
            <input type="email" className="input-vidrio" {...form.register('email')} />
          </Campo>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Teléfono" error={errors.phone?.message}>
              <input className="input-vidrio" inputMode="tel" {...form.register('phone')} />
            </Campo>
            <Campo etiqueta="Género">
              <select className="input-vidrio" {...form.register('gender')}>
                <option value="">Sin especificar</option>
                <option value="M">Mujer</option>
                <option value="H">Hombre</option>
                <option value="N">No binario</option>
              </select>
            </Campo>
          </div>
          <Campo etiqueta="Especialidad objetivo">
            <input className="input-vidrio" {...form.register('targetSpecialty')} />
          </Campo>
          <Campo etiqueta="Escuela de procedencia">
            <input className="input-vidrio" {...form.register('schoolName')} />
          </Campo>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              {...form.register('sendInvite')}
            />
            Enviar el enlace para definir su contraseña
          </label>
        </section>
        <section className="panel-vidrio space-y-4">
          <label className="flex items-center gap-2 text-lg font-extrabold">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={conLicencia}
              onChange={(e) => setConLicencia(e.target.checked)}
            />
            Otorgar una licencia
          </label>
          {conLicencia && <FormLicencia valor={licencia} onCambio={setLicencia} />}
        </section>
        <div className="xl:col-span-2">
          <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
            {crear.isPending ? 'Creando…' : 'Crear alumno'}
          </button>
        </div>
      </form>
    </>
  );
}

export function NuevoAlumnoPage() {
  return (
    <PermissionGate seccion="alumnos" nivel="editar">
      <Nuevo />
    </PermissionGate>
  );
}
